'use strict';

import Mustache from 'mustache';
import { getFocusableChildren } from '../util/focusable';

/**
 * Generic overlay handler.
 */
class OverlayHandler {

	constructor() {
		global._overlayHandlerPopupQueue = global._overlayHandlerPopupQueue || [];
		global._overlayHandlerCloseQueue = global._overlayHandlerCloseQueue || [];
		global._overlayHasPopListener = global._overlayHasPopListener || false;
		global._overlayHasEscListener = global._overlayHasEscListener || false;
		this.settings = {
			transitionTime: 300,
			transitionStyle: 'ease',
			addToQueue: false, // Popup opens after closing all active popups.
			priorityPopup: false, // Popup opens on top of current popup. On close the previous popup will be restored.
			popupQueue: global._overlayHandlerPopupQueue,
			closeQueue: global._overlayHandlerCloseQueue, // Close order.
			initialContent: null, // Initial content
			returnToContainer: false, // Return to original container on close (instead of destroying)
			trigger: null // Which element triggered overlay (after close returning focus to this element)
		};

		global._OverlayHandler = global._OverlayHandler || this;
	}

	set settings(settings) {
		if (typeof settings === 'object' && settings) {

			if (typeof this._settings !== 'object') {
				this._settings = {};
			}
			for (const key of Object.keys(settings)) {
				this._settings[key] = settings[key];
			}
		}
	}

	get settings() {
		return this._settings;
	}

	closeOverlay(overlayPopup, settings) {

		const popupSettings = JSON.parse(JSON.stringify(this.settings));
		if (typeof settings === 'object' && settings) {
			for (const key of Object.keys(settings)) {
				popupSettings[key] = settings[key];
			}
		}

		overlayPopup.classList.remove('visible');
		if (this.settings.closeQueue.length < 1) {
			overlayPopup.parentNode.classList.remove('active');
			document.body.classList.remove('has-overlay');
		}
		const cleanup = typeof popupSettings.cleanup === 'function' ? popupSettings.cleanup : function () { return true; };

		setTimeout(() => {
			let focusSet = false;

			if (popupSettings.initialContent && popupSettings.returnToContainer && typeof popupSettings.returnToContainer === 'object' && popupSettings.returnToContainer.tagName !== undefined) {
				// Return to parent container
				popupSettings.returnToContainer.appendChild(popupSettings.initialContent);
			} else {
				// Remove content
				overlayPopup.parentNode.removeChild(overlayPopup);
			}

			if (popupSettings.priorityPopup === true) {
				const popups = document.querySelectorAll('.overlay-wrapper .overlay-popup.snoozed');

				if (popups.length > 0) {
					popups[popups.length - 1].classList.remove('snoozed');
					focusSet = true;

					setTimeout(() => {
						const newPopupClose = popups[popups.length - 1].querySelector('.close-overlay-popup');
						newPopupClose.focus();
					}, 1000);
				}
			}

			if (this.settings.closeQueue.length < 1) {
				const overlayWrapper = document.querySelector('.overlay-wrapper');
				if (overlayWrapper) {
					overlayWrapper.parentNode.removeChild(overlayWrapper);
				}

				if (global._overlayHasPopListener) {
					window.removeEventListener('popstate', global._OverlayHandler.handleBackButton);
					global._overlayHasPopListener = false;
				}

				if (global._overlayHasEscListener) {
					window.removeEventListener('keydown', global._OverlayHandler.handleEscButton);
					global._overlayHasEscListener = false;
				}
			}

			cleanup(overlayPopup);

			if (this.settings.popupQueue.length > 0) {
				this.closeActiveOverlay(true);
			} else if(!focusSet && popupSettings) {
				popupSettings.trigger.focus();
			}

		}, popupSettings.transitionTime + 1);

	}

	closeActiveOverlay(first) {
		if (first === true) {
			this.settings.popupQueue.shift()();
		} else {
			this.settings.closeQueue.pop()();
		}
	}

	handleBackButton() {
		global._OverlayHandler.closeActiveOverlay();
	}

	handleEscButton(e) {
		if (e.key === 'Escape') {
			global._OverlayHandler.closeActiveOverlay();
		}
	}

	createOverlay(content, settings) {

		const popupSettings = JSON.parse(JSON.stringify(this.settings));
		if (typeof settings === 'object' && settings) {
			for (const key of Object.keys(settings)) {
				popupSettings[key] = settings[key];
			}
		}


		// Prevents adding multiple overlays.
		if (popupSettings.priorityPopup !== true) {

			if (document.querySelector('.overlay-wrapper')) {
				if (popupSettings.addToQueue !== true) {
					return false;
				} else {
					this.settings.popupQueue.push(() => {
						this.createOverlay(content, popupSettings);
					});
					return true;
				}
			}
		} else {
			const popups = document.querySelectorAll('.overlay-wrapper .overlay-popup');
			if (popups.length > 0) {
				for (const popup of popups) {
					popup.classList.add('snoozed');
				}
			}
		}

		let addToBody = false;
		let overlayWrapper = document.querySelector('.overlay-wrapper');
		if (!overlayWrapper) {
			addToBody = true;
			overlayWrapper = document.createElement('div');
			overlayWrapper.classList.add('overlay-wrapper');
			const overlayMask = document.createElement('div');
			overlayMask.classList.add('overlay-mask');
			overlayMask.style.transition = 'background-color ' + popupSettings.transitionTime + 'ms ' + popupSettings.transitionStyle;
			overlayMask.title = 'Sulje näkymä';
			overlayWrapper.appendChild(overlayMask);
			overlayMask.addEventListener('click', (e) => {
				e.preventDefault();
				this.closeActiveOverlay();
			});
		}


		const overlayPopup = document.createElement('aside');
		overlayPopup.setAttribute('role', 'dialog');
		overlayPopup.setAttribute('aria-modal', 'true');
		overlayPopup.className = 'overlay-popup components';
		overlayPopup.style.transition = 'transform ' + popupSettings.transitionTime + 'ms ' + popupSettings.transitionStyle + ', opacity ' + popupSettings.transitionTime + 'ms ' + popupSettings.transitionStyle + ', visibility ' + popupSettings.transitionTime + 'ms ' + popupSettings.transitionStyle;

		if (popupSettings.additionalClasses) {
			if (Array.isArray(popupSettings.additionalClasses)) {
				for (const cssClass of popupSettings.additionalClasses) {
					if (cssClass) {
						overlayPopup.classList.add(String(cssClass));
					}
				}
			} else if (popupSettings.additionalClasses) {
				overlayPopup.classList.add(String(popupSettings.additionalClasses));
			}
		}

		if (popupSettings.keyboardFocus) {
			overlayPopup.classList.add('keyboard-focus');
		}

		const closeButton = document.createElement('button');
		closeButton.title = popupSettings.closeText || 'Takaisin';
		closeButton.innerHTML = '<i class="is-icon is-icon-arrow is-icon-arrow-left" aria-hidden="true"></i> '+closeButton.title;
		closeButton.className = 'close-overlay-popup mfp-close a-link';

		closeButton.addEventListener('click', (e) => {
			e.preventDefault();
			this.settings.closeQueue.pop()();
		});

		if (!global._overlayHasEscListener) {
			window.addEventListener('keydown', global._OverlayHandler.handleEscButton);
			global._overlayHasEscListener = true;
		}

		const contentWrapper = document.createElement('div');
		contentWrapper.classList.add('content-wrapper');
		if (typeof content === 'object' && content.tagName !== undefined) {
			// Save parent container & initial content
			if (popupSettings.returnToContainer) {
				popupSettings.initialContent = content;
				popupSettings.returnToContainer = content.parentNode;
			}
			contentWrapper.appendChild(content);
		} else {
			contentWrapper.innerHTML = popupSettings.mustache === true ? Mustache.render(content, popupSettings.view || {}) : content;
		}

		// Focus traps keep tab focus inside the dialog (a11y)
		const focusTrapStart = document.createElement('div');
		focusTrapStart.classList.add('focus-trap-start');
		focusTrapStart.tabIndex = 0;

		const focusTrapEnd = document.createElement('div');
		focusTrapStart.classList.add('focus-trap-end');
		focusTrapEnd.tabIndex = 0;

		focusTrapStart.addEventListener('focus', (e) => {
			// Focus to last focusable element
			const focusableChildren = getFocusableChildren(contentWrapper);
			if (focusableChildren.length) {
				const lastFocusableChild = focusableChildren[focusableChildren.length - 1];
				lastFocusableChild.focus();
				lastFocusableChild.scrollIntoView();
			} else {
				closeButton.focus();
			}
		});

		focusTrapEnd.addEventListener('focus', (e) => {
			// Focus close button
			closeButton.focus();
		});

		// Add extra layer
		const contentContainer = document.createElement('div');
		contentContainer.setAttribute('role', 'document'); // Allows default keyboard actions inside dialog
		contentContainer.classList.add('content-container', 'js-overlay-content');
		contentContainer.appendChild(focusTrapStart);
		contentContainer.appendChild(closeButton);
		contentContainer.appendChild(contentWrapper);
		contentContainer.appendChild(focusTrapEnd);

		overlayPopup.appendChild(contentContainer);
		overlayWrapper.appendChild(overlayPopup);

		const init = typeof popupSettings.init === 'function' ? popupSettings.init : function () { return true; };
		this.settings.closeQueue.push(() => { this.closeOverlay(overlayPopup, popupSettings); });


		if (addToBody) {
			document.body.appendChild(overlayWrapper);
		}
		// Do something after initialsing popup.
		init(overlayPopup);

		setTimeout(() => {
			document.body.classList.add('has-overlay');
			overlayPopup.parentNode.classList.add('active');
			overlayPopup.classList.add('visible');
		}, 50);

		if (!global._overlayHasPopListener) {
			window.history.pushState(null, '');
			window.addEventListener('popstate', global._OverlayHandler.handleBackButton);
			global._overlayHasPopListener = true;
		}

		// Moves focus to ovelay basically (must have a considerable timeout for a11y).
		setTimeout(() => {
			closeButton.focus();
		}, 1000);

		return overlayPopup;
	}
}

export { OverlayHandler };

const Overlay = new OverlayHandler();

/**
* Load modal with ajax content from specified url
*
* @param {HTMLElement} elem - Link element that was clicked
*/
function loadModal(elem) {

	if (!elem) {
		return false;
	}
	// Link url
	const baseUrl = elem.href;
	// Ajax url might be different
	const ajaxUrl = elem.dataset.href || baseUrl;

	// contentonly parameter will strip header and footer and leave only content
	const url = ajaxUrl; // + (ajaxUrl.indexOf('?') !== -1 ? '&' : '?') + 'contentonly=1';

	// Custom classes for modal popup
	let classes = elem.dataset.modalClasses || '';
	classes += ' overlay-popup--ajax overlay-popup--loading';

	// Create modal
	const modal = Overlay.createOverlay('<div class="loader"></div>', { trigger: elem, additionalClasses: classes.split(' ') });

	fetch(url, {
		method: 'GET', // *GET, POST, PUT, DELETE, etc.
		//mode: 'cors', // no-cors, *cors, same-origin
		//cache: 'default', // *default, no-cache, reload, force-cache, only-if-cached
		//credentials: 'same-origin', // include, *same-origin, omit
		headers: {
			// Because Stage ajax tempalte has this:
			/*
				if(Server.HTTP_X_REQUESTED_WITH != 'XMLHttpRequest' && !$.isEditor()) {
					showBasicHeader = true;
				}
			*/
			// and I'm to lazy to change that.
			'x-requested-with': 'XMLHttpRequest'
		},
		//redirect: 'follow', // manual, *follow, error
		//referrerPolicy: 'no-referrer-when-downgrade', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
	}).then(response => {
		if (!response.ok) {
			throw new Error('Network response error.');
		}
		return response.text();
	}).then(data => {
		modal.classList.add('overlay-popup--ajax-loaded');
		modal.classList.remove('overlay-popup--loading');
		modal.querySelector('.content-wrapper').innerHTML = data;
	}).catch(() => {
		modal.classList.add('overlay-popup--ajax-loaded');
		modal.classList.remove('overlay-popup--loading');
		modal.querySelector('.content-wrapper').innerHTML = '<p style="text-align: center;">Sisällön lataamisessa tapahtui virhe.</p>';
	});

	return true;
}

/**
 * Open modal with content from element
 *
 * @param {HTMLElement} elem - Link element that was clicked
 */
function openModal(elem) {

	if (!elem) {
		return false;
	}

	const target = elem.dataset.target;

	if (target) {
		const targetElement = document.querySelector(target);
		if (targetElement) {
			// Custom classes for modal popup
			const classes = elem.dataset.modalClasses || '';

			// Create modal
			Overlay.createOverlay(targetElement.innerHTML, { trigger: elem, additionalClasses: classes.split(' ') });
		}
	}

	return true;
}

export function OverlayInit(API) {

	API.log('INIT Overlays');

	// Open anchor target url as a modal
	// Note: will append contentonly=1 param,
	// which tells Stage template to exclude header, footer, etc and only retrieve content html

	window.addEventListener('click', (e) => {
		const target = e.target;
		const clickedElement = target.closest('a.open-modal, [data-toggle="modal-overlay"]');
		if (clickedElement) {
			e.preventDefault();
			const href = clickedElement.getAttribute('href');

			if (clickedElement.tagName === 'A' && href && href !== '#') {
				loadModal(clickedElement);
			} else {
				openModal(clickedElement);
			}

		}

	});

}

export default { OverlayInit };
